/**
 * Build a stable, deterministic CSS selector for a DOM element.
 *
 * Adapted from the earlier `packages/core/src/dom/elementPath.ts`. The
 * shape is simplified for AFS 1.1: we expose `elementPath` (unique CSS)
 * and `fullPath` (uniqueness-guaranteed XPath). Smart human-readable
 * naming lives in `smartLabel.ts`.
 *
 * Unstable identifiers and hashed class names are skipped so the
 * selector survives re-renders and CSS-in-JS rebuilds.
 */
const UNSTABLE_ID_PATTERNS = [
    /^ember\d+$/i,
    /^[a-z]+-:r[0-9a-z]+:$/i,
    /^:r[0-9a-z]+:$/i,
    /^uid-[0-9a-z-]+$/i,
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
];
const UNSTABLE_CLASS_PATTERNS = [
    /^css-[a-z0-9]+$/i,
    /^[a-z0-9_-]+__[a-z0-9_-]+--[a-z0-9_-]+$/i,
    /^[a-z]+-module[_-][a-z0-9_-]+__[a-z0-9]+$/i,
    /^jsx-\d+$/,
    /^sc-[a-z0-9]+$/i,
    /^_[a-z0-9]{5,}$/i,
    /^[a-z0-9]+\$[a-z0-9]+$/i,
];
export const isStableId = (id) => {
    if (!id)
        return false;
    if (id.includes(":"))
        return false;
    return !UNSTABLE_ID_PATTERNS.some((p) => p.test(id));
};
export const stableClasses = (el) => {
    const out = [];
    for (const c of Array.from(el.classList)) {
        if (UNSTABLE_CLASS_PATTERNS.some((p) => p.test(c)))
            continue;
        out.push(c);
    }
    return out;
};
const cssEscape = (value) => {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(value);
    }
    return value.replace(/([^\w-])/g, "\\$1");
};
const buildCssSegment = (el) => {
    let segment = el.tagName.toLowerCase();
    if (el.id && isStableId(el.id)) {
        return `${segment}#${cssEscape(el.id)}`;
    }
    const classes = stableClasses(el);
    if (classes.length > 0) {
        segment += "." + classes.map(cssEscape).join(".");
    }
    const parent = el.parentElement;
    if (parent) {
        const siblings = Array.from(parent.children).filter((s) => s.tagName === el.tagName);
        if (siblings.length > 1) {
            const idx = siblings.indexOf(el) + 1;
            segment += `:nth-of-type(${idx})`;
        }
    }
    return segment;
};
const isSelectorUnique = (root, selector) => {
    try {
        return root.querySelectorAll(selector).length === 1;
    }
    catch {
        return false;
    }
};
export const getCssSelector = (el, root = el.ownerDocument ?? document) => {
    const segments = [];
    let current = el;
    while (current && current.nodeType === Node.ELEMENT_NODE) {
        const segment = buildCssSegment(current);
        segments.unshift(segment);
        if (current.id && isStableId(current.id))
            break;
        const candidate = segments.join(" > ");
        if (segments.length > 1 && isSelectorUnique(root, candidate)) {
            return candidate;
        }
        current = current.parentElement;
        if (!current || current === root)
            break;
    }
    return segments.join(" > ");
};
export const getXPath = (el) => {
    if (el.id && isStableId(el.id))
        return `//*[@id="${el.id}"]`;
    const segments = [];
    let current = el;
    while (current && current.nodeType === Node.ELEMENT_NODE) {
        const tag = current.tagName.toLowerCase();
        const parent = current.parentElement;
        if (!parent) {
            segments.unshift(tag);
            break;
        }
        const sameTagSiblings = Array.from(parent.children).filter((s) => s.tagName === current.tagName);
        if (sameTagSiblings.length === 1) {
            segments.unshift(tag);
        }
        else {
            const idx = sameTagSiblings.indexOf(current) + 1;
            segments.unshift(`${tag}[${idx}]`);
        }
        current = parent;
    }
    return "/" + segments.join("/");
};
export const getDomString = (el, maxDepth = 12) => {
    const segments = [];
    let current = el;
    let depth = 0;
    while (current && depth < maxDepth) {
        let s = current.tagName.toLowerCase();
        if (current.id)
            s += `#${current.id}`;
        const cls = stableClasses(current).slice(0, 3);
        if (cls.length > 0)
            s += "." + cls.join(".");
        segments.unshift(s);
        current = current.parentElement;
        depth += 1;
    }
    return segments.join(" > ");
};
//# sourceMappingURL=elementPath.js.map